import { useAppDispatch, useAppSelector, useFeatureToggle, useUserProfilePics } from "@hooks";
import {
  Desk,
  DeskBookingStatus,
  ReservationMember,
  getAreaById,
  getFacilitiesByFloorId,
  getWorkspaceReservationsForConnectionsByDate,
  loadFacilities,
  withAsyncThunkErrorHandling,
} from "@lib/store";
import { CenteredLoader, StyledCard } from "./styles";
import { MapSelectorCardProps, ToastData } from "./types";
import { useEffect, useMemo, useRef, useState } from "react";
import { EventTarget } from "../../../../../../../submodules/map/mapiq-map/EventTarget";
import { toMapViewUser } from "@utils";
import { MapToast, MapView } from "@molecules";
import { useTheme } from "styled-components";
import { BuildingNodeState } from "../../../../../../../submodules/map/mapiq-map/MapState";
import {
  MapViewHighlight,
  MapViewUser,
  anonymousSelectedAreaState,
  desksState,
  mergeState,
  selectedAreaState,
} from "@lib/utils";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { LocalTimespan, OfficeWorkday, WorkspaceReservation } from "@lib/store/src/data/timeslots/apiTypes";
import { useReservationDetails } from "@lib/store/src/ui/hooks/timeslots/useReservationDetails";

export const MapSelectorCard2 = (
  props: MapSelectorCardProps & {
    workday?: OfficeWorkday;
    workspaceReservation?: WorkspaceReservation;
    timespan: LocalTimespan;
  },
) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { areaId, date, workday, workspaceReservation, timespan } = props;

  const connectionShifts = useAppSelector(state => {
    const entry = state.data.timeslots.connectionWorkspaceReservationsByDate[date];
    if (entry?.status !== "fulfilled") return [];
    return entry.data.filter(d => "areaId" in d.workspace && d.workspace.areaId === areaId);
  });

  const connectionProfileImages = useUserProfilePics(
    connectionShifts.map(r => ({
      userId: r.owner.id,
      imageHash: r.owner.imageHash ?? "",
    })),
  );

  const connectionsByDeskId = useMemo(() => {
    return new Map<string, MapViewUser>(
      connectionShifts.flatMap(r => {
        if (!("deskId" in r.workspace)) return [];
        const connection = toMapViewUser(
          {
            email: r.owner.email,
            imageUrl: connectionProfileImages[r.owner.id],
            initials: r.owner.initials,
          },
          theme,
        );

        return [[r.workspace.deskId, connection]];
      }),
    );
  }, [connectionShifts, theme, connectionProfileImages]);

  // Figuring out which desks are available for picking

  // Note: I don't trust the store to have desks that are up to date... maybe I should?
  //       Opted to handle the requests in the component instead
  const areaNode = useAppSelector(state => getAreaById(state, areaId));
  const deskBookingRequired = Boolean(areaNode?.hasBookableDesks);
  const [loading, setLoading] = useState(true);
  const [desks, setDesks] = useState<Desk[]>([]);
  const [bookedDesksById, setBookedDesksById] = useState<DeskBookingStatus>({});

  const details = useReservationDetails(workspaceReservation || null);

  // this will remove desks that are used by the current reservation
  const deskIdsBookedByOthers = useMemo((): DeskBookingStatus => {
    if (details.status !== "fulfilled") return bookedDesksById;

    details.data.forEach(d => {
      if (d.reservation.workspace.nodeType === "Desk" && d.reservation.workspace.deskId) {
        delete bookedDesksById[d.reservation.workspace.deskId];
      }
    });

    return bookedDesksById;
  }, [bookedDesksById, details]);

  const features = useFeatureToggle();

  useEffect(
    function autoLoadDesks() {
      let canceled = false;

      const getInfo = async () => {
        const info = await dispatch(
          withAsyncThunkErrorHandling(() =>
            actions.loadDesksByAreaId({
              startDate: timespan.localStart,
              endDate: timespan.localEnd,
              areaId,
            }),
          ),
        );
        if (canceled) return;

        if (info.success && info.result) {
          setDesks(info.result.desks);
          setBookedDesksById(info.result.bookedById);
        } else {
          setDesks([]);
          setBookedDesksById({});
        }

        setLoading(false);
      };

      if (areaId && deskBookingRequired) {
        setLoading(true);
        getInfo();
      } else {
        setLoading(false);
      }

      return () => {
        canceled = true;
      };
    },
    [dispatch, areaId, date, deskBookingRequired, timespan.localStart, timespan.localEnd],
  );

  return (
    <MapSelectorCard_internal
      {...props}
      loading={loading}
      desks={desks}
      deskIdsBookedByOthers={deskIdsBookedByOthers}
      connectionsByDeskId={connectionsByDeskId}
    />
  );
};

// This component takes location ids and optional users and renders them in context.
// If provided with an onClick handler, it forwards map clicks to that handler.
export const MapSelectorCard_internal = ({
  buildingId,
  floorId,
  areaId,
  deskIds,
  users,
  groupSize,
  connectionsByDeskId,
  onClick,
  loading,
  deskIdsBookedByOthers,
  desks,
  toasterData,
}: Omit<MapSelectorCardProps, "date"> & {
  connectionsByDeskId: Map<string, MapViewUser>;

  loading: boolean;

  desks: Array<Desk>;
  deskIdsBookedByOthers: DeskBookingStatus;
}) => {
  const theme = useTheme();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [toastToBeShown, setToastToBeShown] = useState<ToastData | null>(null);
  const dispatch = useAppDispatch();
  const areBuildingAndFloorIdsValid = !!buildingId.length && !!floorId.length;
  const areaNode = useAppSelector(state => getAreaById(state, areaId));
  const deskBookingRequired = Boolean(areaNode?.hasBookableDesks);
  const buildingNodeTypeStates = new Map([["facility", "dimmed"]]) as ReadonlyMap<
    "area" | "desk" | "room" | "facility",
    BuildingNodeState
  >;

  const mapUsers = users.map(user => toMapViewUser(user, theme));

  const facilitiesData = useAppSelector(getFacilitiesByFloorId(floorId));

  const onMapClickHandler = (target: EventTarget) => {
    if (toasterData && deskBookingRequired && target.nodeId && toasterData[target.nodeId]) {
      setToastToBeShown(toasterData[target.nodeId]);

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        setToastToBeShown(null);
      }, 3000);
    }

    onClick?.(target);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (facilitiesData.status === "NotLoaded" && areBuildingAndFloorIdsValid) {
      dispatch(withAsyncThunkErrorHandling(() => loadFacilities({ buildingId, floorId })));
    }
  }, [areBuildingAndFloorIdsValid, buildingId, dispatch, facilitiesData.status, floorId]);

  const areaState = mapUsers.length
    ? selectedAreaState(areaId, mapUsers)
    : anonymousSelectedAreaState(areaId, groupSize ?? 1);

  const { highlights, buildingNodeStates } =
    deskIds === undefined
      ? { highlights: areaState.markers, buildingNodeStates: areaState.state }
      : constructMapState(desks, deskIdsBookedByOthers, connectionsByDeskId, areaId, deskIds, mapUsers, onClick);

  return (
    <StyledCard>
      {loading && facilitiesData.status !== "Loaded" ? (
        <CenteredLoader />
      ) : (
        <>
          <MapView
            buildingId={buildingId}
            floorId={floorId}
            highlights={highlights.concat(
              facilitiesData.status === "Loaded"
                ? facilitiesData.facilities.map(facility => {
                    return {
                      nodeId: facility.id,
                      subType: facility.facilityType.id,
                      type: "facility",
                    };
                  })
                : [],
            )}
            buildingNodeStates={buildingNodeStates}
            buildingNodeTypeStates={buildingNodeTypeStates}
            onClick={onMapClickHandler}
            disablePointerEvents={false}
            fullView={true}
          />
        </>
      )}

      {toastToBeShown && <MapToast toast={toastToBeShown} />}
    </StyledCard>
  );
};

const constructMapState = (
  desks: Desk[],
  bookedDesksById: DeskBookingStatus,
  connectionsByDeskId: Map<string, MapViewUser>,
  selectedAreaId: string,
  selectedDeskIds: string[],
  mapUsers: MapViewUser[],
  onClick?: (target: EventTarget) => void,
) => {
  // Construct our states and markers
  // 1) selections go first; they are managed outside of this component and passed through props
  const { state: selectionState, markers: selectionMarkers } = desksState(selectedDeskIds, mapUsers, "selected");

  // 2) desks not in the selection get a marker by us
  const unselectedDesks = desks.filter(d => d.isEnabled && !selectionState.has(d.id));

  // 2a) available ones
  const availableDeskIds = unselectedDesks.filter(d => bookedDesksById[d.id] === false).map(d => d.id);
  const { state: availableState, markers: availableMarkers } = desksState(
    availableDeskIds,
    [],
    onClick ? "highlighted" : "dimmed",
  );

  // 2b) occupied ones
  const bookedDeskIds = unselectedDesks.filter(d => bookedDesksById[d.id] === true).map(d => d.id);
  const withConnections = bookedDeskIds.filter(id => connectionsByDeskId.has(id));
  const withoutConnections = bookedDeskIds.filter(id => !connectionsByDeskId.has(id));

  // 2b1) by connections
  const { state: connectionsState, markers: connectionsMarkers } = desksState(
    withConnections,
    // TODO: type this better
    withConnections.flatMap(deskId => {
      const connection = connectionsByDeskId.get(deskId);

      // Note: this should not happen because of the filter used to create `withConnections`
      if (!connection) return [];

      return [connection];
    }),
    "dimmed",
  );

  // 2b2 by anonymous colleagues
  const { state: bookedState, markers: bookedMarkers } = desksState(withoutConnections, [], "dimmed", false);

  // 2c) available ones
  const desksYouCanPick = unselectedDesks
    .filter(d => d.isEnabled && !bookedDesksById[d.id])
    .map(
      (d): MapViewHighlight => ({
        type: "desk",
        nodeId: d.id,
        connections: [],
        available: true,
      }),
    );

  // Combine states
  const highlights = [
    ...desksYouCanPick,
    ...selectionMarkers,
    ...connectionsMarkers,
    ...availableMarkers,
    ...bookedMarkers,
  ];
  const buildingNodeStates = [bookedState, connectionsState, availableState, selectionState].reduce(mergeState);

  // Select the area as well
  buildingNodeStates.set(selectedAreaId, "selected");

  return {
    highlights,
    buildingNodeStates,
  };
};
